import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { adminPortalLogin } from '../lib/admin-portal-api';
import { FaExclamationTriangle } from '../lib/icons';

interface Props {
  onLoggedIn: () => void;
}

export default function AdminPortalLogin({ onLoggedIn }: Props) {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      await adminPortalLogin(email.trim(), password);
      onLoggedIn();
      navigate('/admin-portal', { replace: true });
    } catch (err: any) {
      const msg = err?.response?.data?.error?.message || err?.response?.data?.error || err?.message || 'Login failed.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
      <Helmet>
        <title>Admin Portal — EatMeat</title>
      </Helmet>
      <div className="w-full max-w-md bg-white rounded-2xl border border-gray-100 shadow-sm p-8">
        <div className="text-center mb-6">
          <h1 className="text-xl font-extrabold tracking-tight text-gray-900">
            EatMeat <span className="text-primary">Admin</span>
          </h1>
          <p className="text-sm text-gray-500 mt-1">Sign in with your Strapi admin credentials</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700 flex items-start gap-2">
              <FaExclamationTriangle className="mt-0.5 text-red-500 shrink-0" />
              <span>{error}</span>
            </div>
          )}
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">Email</label>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@eatmeatapp.com"
              className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 text-sm focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/10 focus:outline-none transition-all" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">Password</label>
            <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 text-sm focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/10 focus:outline-none transition-all" />
          </div>
          <button type="submit" disabled={submitting}
            className="w-full py-3 bg-primary text-white rounded-xl font-semibold hover:bg-primary/90 disabled:opacity-60 transition-colors">
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
          <div className="text-center text-[11px] text-gray-400 mt-3">
            This portal uses the same accounts as <span className="font-semibold">{(process.env.REACT_APP_STRAPI_API_URL || '').replace(/^https?:\/\//,'')}/admin</span>
          </div>
        </form>
      </div>
    </div>
  );
}
